import { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Send, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { format } from 'date-fns';

export default function CoachChat() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [coach, setCoach] = useState(null);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState('');
  const scrollRef = useRef(null);

  const load = async () => {
    const c = await api.get('/coach');
    setCoach(c.data);
    const t = await api.get(`/messages/${c.data.id}`);
    setThread(t.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [thread]);

  const send = async () => {
    if (!body.trim() || !coach) return;
    await api.post('/messages', { to_user_id: coach.id, body });
    setBody('');
    const t = await api.get(`/messages/${coach.id}`);
    setThread(t.data);
  };

  return (
    <div className="-mx-0 -mt-0 min-h-[100vh] flex flex-col bg-background" data-testid="coach-chat">
      {/* Header */}
      <div className="sticky top-0 z-20 glass border-b border-[var(--c-hair)] px-4 py-3 flex items-center gap-3">
        <button onClick={() => nav('/app/settings')} data-testid="chat-back" className="w-9 h-9 rounded-full hover:bg-[var(--c-film)] flex items-center justify-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00C8FF] to-[var(--c-green)] flex items-center justify-center text-[#081726] font-bold">JP</div>
        <div className="flex-1">
          <div className="font-display font-bold leading-tight">{coach?.name || 'Coach JP'}</div>
          <div className="text-[11px] text-[var(--c-green-ink)] flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--c-green)] animate-pulse" /> Active Coach
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 pb-24 lg:px-10 lg:pb-28 lg:max-w-4xl lg:w-full lg:mx-auto">
        {thread.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#00C8FF]/10 flex items-center justify-center mb-3">
              <MessageSquare className="w-7 h-7 text-[#00C8FF]" />
            </div>
            <div className="font-display font-bold text-lg">Say hi to Coach JP</div>
            <div className="text-sm text-muted-foreground mt-1">He'll answer personally. This is your direct line.</div>
          </div>
        )}
        {thread.map((m, idx) => {
          const isMe = m.from_user_id === user.id;
          const showDate = idx === 0 || thread[idx-1].created_at.slice(0,10) !== m.created_at.slice(0,10);
          return (
            <div key={m.id}>
              {showDate && <div className="text-center text-[10px] uppercase tracking-widest text-muted-foreground my-3">{format(new Date(m.created_at), 'MMM d')}</div>}
              <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-sm ${isMe ? 'bg-[#00C8FF] text-[#081726] rounded-br-sm' : 'bg-[var(--c-film)] rounded-bl-sm'}`}>
                  {m.body}
                  <div className={`text-[9px] mt-0.5 ${isMe ? 'text-[#081726]/60' : 'text-muted-foreground'}`}>{format(new Date(m.created_at), 'HH:mm')}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <div className="fixed bottom-0 inset-x-0 lg:left-60 glass border-t border-[var(--c-hair)] safe-bottom px-3 py-2 lg:px-10 lg:py-4">
        <div className="max-w-2xl lg:max-w-4xl mx-auto">
        <div className="flex gap-2">
          <Input value={body} onChange={e => setBody(e.target.value)} placeholder="Message Coach JP…" data-testid="chat-input"
            onKeyDown={e => e.key === 'Enter' && send()}
            className="bg-[var(--c-film)] border-[var(--c-hair)] h-11 rounded-full" />
          <Button onClick={send} data-testid="chat-send" className="bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] rounded-full w-11 h-11 p-0">
            <Send className="w-4 h-4" />
          </Button>
        </div>
        </div>
      </div>
    </div>
  );
}
